import React, {FC, useState, useLayoutEffect} from 'react';
import {graphql} from 'gatsby';
import styled, {css} from 'styled-components';

import {fontFamiliesR4, fontSizesMixinsMobileR4, fontSizesMixinsR4, paletteR4, paletteR4Secondary} from '../../../lib/styleVariablesR4';
import {mediaBreakpointUp} from '../../../lib/styleVariables';

import {Header as Navigation} from '../../../templates/contentful-model/header';
import {Footer} from '../../../templates/contentful-model/footer';
import {MetaTags} from '../../../components/common/meta-tags';
import {SecondaryButton} from '../../../components/molecules/buttonsR4';
import {SiteStyles} from '../../../components/common/layout';
import {TopBarR4} from '../../../components/molecules/topbarR4';
import AnimatedBackgroundColor from '../../../components/modules/animatedBackgroundColor';
import {ImageTextCarousel} from '../../../components/modules/imageTextBlocks/carouselR4';
import {SubscriptionOverlay} from '../../../templates/newsletter/subscriptionOverlay';

import LegoBuilderIcon from './_components/lego-builder.urlLoader.svg';
import {ImageTextCarouselItemProps} from '../../../components/modules/imageTextBlocks/carouselR4/item';

const Container = styled.div`
  padding: 72px 32px;
  max-width: 920px;
  margin: 0 auto;
  box-sizing: content-box;

  ${mediaBreakpointUp('desktop')} {
    padding-top: 160px;
    padding-bottom: 160px;
  }
`;

const GradientContainer = styled.div`
  background: linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(255,218,199,1) 100%);

  ${mediaBreakpointUp('desktop')} {
    background: linear-gradient(120deg, rgba(255,255,255,1) 0%, rgba(255,255,255,1) 30%, rgba(255,218,199,1) 100%);

    ${Container} {
      padding-top: 60px;
    }
  }
`;

const Header = styled.header`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 56px;
`;

const HeadlineIcon = styled.img`
  margin: 0 0 8px 0;

  ${mediaBreakpointUp('desktop')} {
    margin-bottom: 30px;
  }
`;

const HeadlineIconText = styled.p`
  ${fontSizesMixinsMobileR4.h4()}

  margin: 0 0 24px 0;

  ${mediaBreakpointUp('desktop')} {
    ${fontSizesMixinsR4.h4()}

    margin-bottom: 34px;
  }
`;

const Headline = styled.h1`
  ${fontSizesMixinsMobileR4.h1()}

  margin: 0 0 13px 0;

  ${mediaBreakpointUp('desktop')} {
    ${fontSizesMixinsR4.h1()}

    margin-bottom: 30px;
  }
`;

const Subheadline = styled.p`
  ${fontSizesMixinsMobileR4.p1()}

  margin: 0;

  ${mediaBreakpointUp('desktop')} {
    ${fontSizesMixinsR4.p1()}

    margin-bottom: 40px;
  }
`;

const MathildeImage = styled.img`
  width: 100%;
  max-width: 480px;
  margin: 0 auto 32px;
  display: block;
  border-radius: 100%;
  background: ${paletteR4.gray.tint};

  ${mediaBreakpointUp('desktop')} {
    float: right;
    width: 378px;
    margin: 0 0 16px 16px;
  }
`;

const Paragraph = styled.p`
  ${fontSizesMixinsMobileR4.p2()}

  a {
    color: inherit;
  }

  ${mediaBreakpointUp('desktop')} {
    font-size: 20px;
    line-height: 30px;
  }
`;

const ButtonWrapper = styled.div<{ leftOnDesktop?: boolean }>`
  text-align: center;
  margin-top: 50px;

  ${props => props.leftOnDesktop && css`
    ${mediaBreakpointUp('desktop')} {
      text-align: left;
    }
  `}
`;

const HollowButton = styled(SecondaryButton)`
  display: inline-flex;
  text-decoration: none;
`;

const SectionHeadline = styled.h2<{ color: string }>`
  ${fontSizesMixinsMobileR4.h3()}

  text-align: center;
  color: ${props => props.color};
  margin: 0 0 45px 0;

  ${mediaBreakpointUp('desktop')} {
    ${fontSizesMixinsR4.h2()}
  }
`;

const Accordion = styled(ImageTextCarousel)`
  div {
    margin-bottom: 0;
  }
`;

const Quote = styled.blockquote`
  margin: 0;
  padding: 0;

  color: ${paletteR4Secondary.green.lowlight};
  font-weight: 600;
  text-align: center;

  p {
    font-family: ${fontFamiliesR4.body};
    font-size: 24px;
    line-height: 122%;
    letter-spacing: -1%;
    margin: 0 0 60px 0;

    ${mediaBreakpointUp('desktop')} {
      ${fontSizesMixinsR4.h2()}
    }
  }

  footer {
    font-size: 18px;
    line-height: 24px;
    margin: 0;

    ${mediaBreakpointUp('desktop')} {
      ${fontSizesMixinsR4.h4()}

      br { display: none; }
    }
  }
`;

const Page: FC = (props: any) => {
  const {header} = props.data;
  const {footer} = props.data;

  const [isOverlayOpen, setIsOverlayOpen] = useState(false);

  const newsArticles: ImageTextCarouselItemProps<any>[] = [
    {
      title: 'Forbes',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'Front App CEO Mathilde Collin Wants To Innovate Your Inbox',
      buttonLink: 'https://www.forbes.com/sites/alexkonrad/2020/03/05/long-live-email-san-francisco-startup-front-wants-to-make-you-like-your-inbox-again/?sh=6e3fce3a47f9',
      isOpen: false
    },
    {
      title: 'Inc. Magazine',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'The 100 Women Building America\'s Most Innovative and Ambitious Businesses',
      buttonLink: 'https://www.inc.com/most-innovative-women-2019.html',
      isOpen: true
    },
    {
      title: 'Business Insider',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'A 28-year-old entrepreneur reveals how she snagged $66 million and 10 term sheets in 5 days',
      buttonLink: 'https://www.businessinsider.com/tips-for-raising-venture-capital-for-your-startup-front-ceo-mathilde-collin-2018-6',
      isOpen: false
    },
    {
      title: 'Fortune',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'This CEO Challenged Her Employees to Delete All Non-Essential Apps From Their Phones. Here’s What Happened',
      buttonLink: 'https://fortune.com/2019/10/17/best-companies-to-work-for-priorities-relationships-deleting-apps/',
      isOpen: false
    },
    {
      title: 'Bloomberg',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'How Front is Tackling Email and Workplace Efficiency',
      buttonLink: 'https://www.bloomberg.com/news/videos/2019-04-29/how-front-is-tackling-email-and-workplace-inefficiency-video',
      isOpen: false
    },
    {
      title: 'Fast Company',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'Email is broken, but it’s not going to die anytime soon',
      buttonLink: 'https://www.fastcompany.com/90372337/email-is-broken-but-is-not-going-to-die',
      isOpen: false
    },
    {
      title: 'Wall Street Journal',
      content: undefined,
      imgSrc: undefined,
      buttonText: 'I\'m a Woman CEO and It Doesn\'t Change Anything',
      buttonLink: 'https://www.wsj.com/articles/BL-232B-2905',
      isOpen: false
    }
  ];

  const AllRaises = (
    <a
      href="https://www.forbes.com/sites/bizcarson/2018/04/12/all-raise-female-founder-office-hours/#723d14715fa2"
      target="_blank"
      rel="noopener noreferrer"
    >
      All Raise’s Female Founders Office Hours
    </a>
  );

  const PioneerFund = (
    <a
      href="https://www.nytimes.com/2018/08/09/technology/talent-opportunity-gap-pioneer-fund.html"
      target="_blank"
      rel="noopener noreferrer"
    >
      Pioneer Fund
    </a>
  );

  const Forbes = (
    <a
      href="https://www.forbes.com/sites/amyfeldman/2019/07/16/next-billion-dollar-startups-2019/#74ec6fec1d43"
      target="_blank"
      rel="noopener noreferrer"
    >
      Forbes Next Billion Dollar Startups 2019
    </a>
  );

  const IncFemaleFounders = (
    <a
      href="https://www.inc.com/most-innovative-women-2019.html"
      target="_blank"
      rel="noopener noreferrer"
    >
      Inc Magazine’s Female Founders 100 2019
    </a>
  );

  const Forbes30Under30 = (
    <a
      href="https://www.forbes.com/30-under-30-2017/enterprise-technology/#23f302173e69"
      target="_blank"
      rel="noopener noreferrer"
    >
      Forbes 30 Under 30: Enterprise Tech 2017
    </a>
  );

  const subscriptionFormEmbedData = `{"formId": "6caeb5b9-8b05-4a98-bbf7-1c19efbe3a41","portalId": "4766782"}`;

  useLayoutEffect(() => {
    try {
      const links = document.querySelectorAll<HTMLAnchorElement>('[class*="StyledDesktopCarouselItemWrapper"] a');

      for (let i = 0; i < links.length; i++)
        links[i].target = "_blank";
    } catch (e) {
      // Do nothing
    }
  });

  return (
    <>
      <MetaTags
        lang="en-US"
        title="Mathilde Collin - Front CEO &amp; Co-Founder"
        description="Visit us to read about Mathilde Collin, the CEO &amp; co-founder of Front. Sign up for Mathilde’s Newsletter to join on her journey to grow a world class business"
        keywords={[]}
      />
      <TopBarR4 />
      <Navigation data={header} locationPath={props.path} hasBackgroundColorAnimation />
      <SiteStyles>
        <GradientContainer>
          <Container>
            <Header>
              <HeadlineIcon src={LegoBuilderIcon} alt="" />
              <HeadlineIconText>Introducing</HeadlineIconText>
              <Headline>Mathilde Collin</Headline>
              <Subheadline>CEO and Co-founder at Front</Subheadline>
            </Header>

            <MathildeImage
              src="//images.ctfassets.net/d78dbchf7li1/2IPOrNWhkOLF70fS07yrtp/13aa0caf5d5a44c17505589024cbf130/MathildeFront.png"
              alt="Mathilde Collin, CEO and Co-founder at Front"
            />

            <Paragraph>
              As a child growing up in Paris, France, Mathilde was struck by how many people seemed to be dispassionate about their jobs.
              It felt normal for everybody to dread the next Monday, to complain about the long hours, and to resent their boss.
              It was at an early age that Mathilde committed herself to finding meaningful work—for herself and for the people around her.
            </Paragraph>

            <Paragraph>
              It was this notion that led Mathilde to co-found Front with Laurent Perrin in 2013. Today, Mathilde is CEO of
              Front and has raised more than $138 million in venture capital funding. Front has more than 200 employees and has offices
              located in San Francisco, California, and Paris, France. Years later, their company mission remains the same: to help
              people everywhere work happier.
            </Paragraph>

            <Paragraph>
              Mathilde is known for her values-driven leadership style and for championing top-down radical transparency. Before founding
              Front and moving to San Francisco for Y Combinator in 2014, Mathilde received a Master’s degree at HEC School of Management. Today she
              mentors aspiring entrepreneurs through {AllRaises} and the {PioneerFund}. She has been recognized in {Forbes}, {IncFemaleFounders},
              and {Forbes30Under30}.
            </Paragraph>

            <ButtonWrapper leftOnDesktop>
              <HollowButton type="button" componentType="button" onClick={() => setIsOverlayOpen(true)} transparentBackground>
                Sign up for my newsletter
              </HollowButton>
            </ButtonWrapper>
          </Container>
        </GradientContainer>

        <AnimatedBackgroundColor backgroundColor={paletteR4Secondary.blue.tint!}>
          <Container>
            <SectionHeadline color={paletteR4.red.lowlight!}>News articles about Mathilde</SectionHeadline>
            <Accordion
              items={newsArticles}
              title=""
              eyebrow=""
              imagePosition="left"
            />
          </Container>
        </AnimatedBackgroundColor>

        <AnimatedBackgroundColor backgroundColor={paletteR4Secondary.yellow.tint!}>
          <Container>
            <Quote>
              <p>“The one practice that helps me stay focused on what matters is taking a block of time every Thursday afternoon to step
                back—both literally and figuratively. My best ideas for Front have come during this time.”
              </p>
              <footer>Mathilde Collin,&nbsp;<br />Front Co-Founder and CEO</footer>
            </Quote>
          </Container>
        </AnimatedBackgroundColor>

        <AnimatedBackgroundColor backgroundColor={paletteR4Secondary.orange.tint!}>
          <Container>
            <SectionHeadline color={paletteR4.blue.lowlight!}>Sign up for my newsletter</SectionHeadline>
            <Paragraph css="text-align: center">Sign up for my newsletter to join me on my journey in growing a world-class business,
            leading a fulfilled team, and finding what makes people happy at work.
            </Paragraph>
            <ButtonWrapper>
              <HollowButton type="button" componentType="button" onClick={() => setIsOverlayOpen(true)} transparentBackground>
                Sign up for my newsletter
              </HollowButton>
            </ButtonWrapper>
          </Container>
        </AnimatedBackgroundColor>

        <SubscriptionOverlay
          key={`${isOverlayOpen}`}
          isOpen={isOverlayOpen}
          onClose={() => setIsOverlayOpen(false)}
          title="Sign up for Mathilde’s Musings 💌"
          subscriptionFormEmbedData={subscriptionFormEmbedData}
        />

        <Footer data={footer} />
      </SiteStyles>
    </>
  );
};

export default Page;

export const query = graphql`
  query MathildePage {
    header: contentfulCommonJsonData(contentful_id: {eq: "7tW6ihv7EKcLJXbYJt5PGV"}) {
      ...HeaderMenu
    }
    footer: contentfulCommonJsonData(contentful_id: {eq: "5xq6df3Rlh9HD8O4SnJsdi"}) {
      ...Footer
    }
  }
`;
